import { getUserVisibleOrDefaultExceptionMessage } from '@paragon/client-tools';
import {
  Bearing,
  CalculatedLine,
  Component,
  ComponentDesign,
  DivisionLine,
  isTruss,
  JackGirderConnection,
  LineLocation,
  LoadCase,
  Lumber,
  ManualLoadSettings,
  MaterialSettingsPair,
  Member,
  MemberLine,
  MemberType,
  MovingLoadSettings,
  ParallelLine,
  ParapetManualLoadSettings,
  PerpendicularLine,
  PlateGeometryType,
  PlatePair,
  PlatePlacement,
  PlateType,
  RelativeBraceGeometry,
  RelativeMemberGeometry,
  RelativeNonBraceMemberGeometry,
  TrussDefinitionType,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import {
  angleEquals,
  angleGreaterThanOrEqual,
  convertRadiansToDegrees,
  Direction2D,
  distanceEquals,
  Line2D,
  LineSegment2D,
  Point2D,
  rightAngle,
  straightAngle,
} from '@paragon/geometry-library';
import { Point2DInterface } from '@paragon/geometry-library/source/scripts/Point2D';
import {
  getMostCommonPlateType,
  getTangiblePlateTypesForComboPlatePair,
  isComboPlateType,
  plateTypeWithTangibleTypesEqualityComparator,
} from '@paragon/material-lists';
import { enqueueError, enqueueInfo, enqueueSuccess } from '@paragon/notification-tools';
import {
  calculateLine,
  cutMember,
  CutMemberOptions,
  findAndCalculateLine,
  getAllMembers,
} from '@paragon/relative-geometry';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { calculateMemberMidline } from '@paragon/three-shared';
import { assertExhaustiveSwitch, groupTransitively, KeyToValueOfType, notNullish } from '@paragon/utilities';

import { getPlateDimensionOptions } from '../../components/interface/subpanels/properties/PlatePairProperties';
import { defaultLumber } from '../../defaultLumber';
import { ReferenceLineWithIntersectionComponents } from '../../library/relativeGeometry/getReferenceLineEditableIntersectionPoints';
import pushUniqByGuid from '../../library/utilities/pushUniqByGuid';
import { scrollTo } from '../../scrolling';
import { getBasicInsertionInformation } from '../selectors/basicInsertionSelectors';
import { BearingInsertion, calculateBearingInsertionInfo } from '../selectors/bearingInsertionSelectors';
import { getActiveComponentDesign, getActiveComponentDesignGuid } from '../selectors/getActiveComponentDesignSelector';
import { getActiveComponent } from '../selectors/getActiveComponentSelector';
import { getLoadCasesForActiveComponentDesign } from '../selectors/getLoadCasesForActiveComponentDesignSelector';
import { getNextManuallyInsertedPlatePairName } from '../selectors/getNextManuallyInsertedPlatePairName';
import { getReferenceLinesForActiveComponent } from '../selectors/getReferenceLinesForActiveComponent';
import { getSingleSelectedItemEntryOrNull } from '../selectors/getSelectedItemSelector';
import { getUniquePlatesInMaterialSetForActiveComponentDesign } from '../selectors/getUniquePlatesInPlateList';
import { getInsertingObjectInfo } from '../selectors/insertionObjectSelectors';
import { isInsertingComponentDesign } from '../selectors/isInsertingComponentDesignSelector';
import { isLayoutModeEnabled } from '../selectors/layoutModeSelectors';
import { getPickingObjectInfo, getPickingObjectInfoType } from '../selectors/pickingSelectors';
import { getRightSidebarInfo } from '../selectors/rightToolbarSelectors';
import { getInsertionWorkLineType } from '../selectors/workLineInsertionSelectors';
import {
  applicationOnInsertingGusset,
  applicationOnInsertingLoadCase,
  applicationOnInsertingMemberStack,
  applicationOnInsertingPlatePair,
  applicationOnInsertingReferenceLine,
  applicationOnInsertingSplice,
  applicationOnPickingBraceLocation,
  applicationOnPickingGussetLocation,
  applicationOnPickingMemberCutLineGuid,
  applicationOnPickingReferenceLine,
  applicationOnPickingReferenceLineIntersection,
  applicationOnStartDropTopChords,
  applicationStartInsertBearing,
  clearInsertingObject,
  clearPickingObject,
  InsertingPlatePairInfo,
  isInsertingBearing,
  setBasicInsertionPlurality,
  setDimensionMode,
  setWorkLineInsertionSettings,
  startInsertMember as startInsertMemberRaw,
  updateInsertionPlatePair,
} from '../slices/applicationSlice';
import {
  commitBearing,
  commitMembers,
  setAdditionalLoads,
  setComponent,
  setMembers,
  upsertPlatePairs,
} from '../slices/componentDesignsSlice';
import { setComponentDesignTimestamps } from '../slices/componentDesignTimestampsSlice';
import { clearEditInProgress } from '../slices/editorSlice';
import { setJackGirderConnections } from '../slices/jackGirderConnectionsSlice';
import {
  abortSelectFiles as abortComponentDesignInsertion,
  setUnsavedComponentDesignFromServer,
  updateFeatures,
} from './appActions';
import { ReferenceLineProducer } from './basicInsertionActions';
import { setRenderModeAndEnsureSelectedItemIsAppropriate } from './cameraActions';
import { setMemberLine, stackChord } from './editMemberActions';
import { findAndSetReferenceLine, setEditInProgress, updateActiveGableSettings } from './editorActions';
import { autoCutRecentlyInsertedPlanes } from './layout/roofPlaneActions';
import {
  loadAdditionalLumberInformation,
  loadAdditionalPlatePairInformation,
  loadMaterialSettingsWithSpinner,
} from './loadActions';
import { generateLoadCasesOfActiveComponentDesign } from './paragonProcessActions';
import { selectItem, selectItems } from './selectActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

export const finishReferenceLinesInsertion =
  (lines: CalculatedLine[]): ThunkActionVoid =>
  (dispatch, getState) => {
    let state = getState();
    const { insertingObjectInfo } = state.application;
    const activeComponentDesign = getActiveComponentDesign(state);

    if (activeComponentDesign == null || insertingObjectInfo?.type !== 'referenceLine') {
      throw new Error('Invalid state');
    }

    if (activeComponentDesign.component.referenceLines == null) {
      throw new Error('Component does not have reference lines.');
    }

    dispatch(
      setComponent({
        componentDesignGuid: activeComponentDesign.guid,
        component: {
          ...activeComponentDesign.component,
          referenceLines: lines.reduce(
            (componentLines, line) => pushUniqByGuid(componentLines, line),
            activeComponentDesign.component.referenceLines!,
          ),
        },
      }),
    );

    dispatch(clearInsertingObject());

    state = getState();

    const selectedItemType = getSingleSelectedItemEntryOrNull(state)?.type;
    const hasGeometricObjectSelected =
      selectedItemType === 'member' || selectedItemType === 'brace' || selectedItemType === 'bearing';

    if (!hasGeometricObjectSelected) {
      dispatch(selectItems(lines, 'referenceLine'));

      if (state.panelData.rightToolBarOpen) {
        scrollTo('selectedItemProperties');
      }
    }

    dispatch(enqueueSuccess(`Reference line${lines.length > 1 ? 's' : ''} successfully added.`));
  };

/**
 * Start inserting a reference line in `application.insertingObjectInfo`. It is possible to insert reference lines other
 * ways, like during member and bearing insertion.
 */
export const startInsertReferenceLine = (): ThunkActionVoid => (dispatch, getState) => {
  let state = getState();

  const activeComponentDesign = getActiveComponentDesign(state);
  if (!activeComponentDesign) return;

  if (!activeComponentDesign.component.referenceLines) {
    dispatch(enqueueError('Cannot add reference lines to a component that does not have reference lines.'));
    return;
  }

  const { insertingObjectInfo } = state.application;
  if (insertingObjectInfo && insertingObjectInfo.type !== 'referenceLine') {
    dispatch(cancelInsertion());
  }

  // Ensure the component design is selected.
  dispatch(selectItem(activeComponentDesign, 'componentDesign', { force: true }));

  dispatch(applicationOnInsertingReferenceLine());

  state = getState();
  if (getRightSidebarInfo(state).mode !== 'add-item') {
    dispatch(enqueueInfo('Click a line or intersection to begin drawing a reference line.'));
  }
};

export const getNextReferenceLineName = (existingNames: Set<string>, prefix = 'A') => {
  let name: string | null;
  for (let index = 1; ; index += 1) {
    name = `${prefix}${index}`;
    if (!existingNames.has(name)) {
      break;
    }
  }
  return name;
};

export const startInsertMember =
  ({ isBrace }: { isBrace: boolean }): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const activeComponentDesign = getActiveComponentDesign(getState());
    if (!activeComponentDesign) return;

    if (!activeComponentDesign.component.referenceLines) {
      dispatch(enqueueError('Cannot add members to a component that does not have reference lines.'));
      return;
    }

    // Dimension mode isn't yet selector based, so make sure it's off.
    dispatch(setDimensionMode(false));

    // Ensure the component design is selected.
    dispatch(selectItem(activeComponentDesign, 'componentDesign', { force: true }));

    dispatch(startInsertMemberRaw({ isBrace }));

    if (isBrace) {
      dispatch(applicationOnPickingBraceLocation());
      dispatch(enqueueInfo('Click a point on a member to insert.'));
    } else {
      dispatch(enqueueInfo('Click an intersection or line to begin drawing a member.'));
    }
  };

function createMember(properties: {
  lumber: Lumber;
  thickness: number;
  relativeGeometry: RelativeMemberGeometry;
  name: string;
  memberType: MemberType;
  isFieldInstalled: boolean;
}): Member {
  return {
    guid: crypto.randomUUID(),
    lumber: properties.lumber,
    geometry: [],
    thickness: properties.thickness,
    relativeGeometry: properties.relativeGeometry,
    name: properties.name,
    assemblyName: properties.name,
    memberType: properties.memberType,
    angle: 0,
    pitch: '',
    crossSectionalArea: '',
    midlineStartX: 0,
    midlineStartY: 0,
    midlineStartZ: 0,
    midlineEndX: 0,
    midlineEndY: 0,
    midlineEndZ: 0,
    leftBevelCut: null,
    rightBevelCut: null,
    bottomBevelCut: null,
    isFieldInstalled: properties.isFieldInstalled,
  };
}

export const finishInsertingNonBraceMembers =
  (lineProducers: ReferenceLineProducer[], locationHint: Point2DInterface | undefined): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const componentDesign = getActiveComponentDesign(getState());
    if (!componentDesign) throw new Error('Active component design not found.');

    const { component } = componentDesign;
    if (!isTruss(component)) {
      dispatch(enqueueError('Adding members to walls is not currently supported.'));
      return;
    }

    const memberType = 'Web';

    const existingMembers = getAllMembers(component);
    const existingMemberNames = existingMembers.map((member) => member.name);

    const createdMemberLines = lineProducers.map((createMemberLine) => {
      const guid = crypto.randomUUID();
      const name = getNewMemberName(memberType, existingMemberNames);

      existingMemberNames.push(name);

      return createMemberLine(guid, name);
    });

    const materialSettings = await dispatch(loadMaterialSettingsWithSpinner(componentDesign.guid));
    if (!materialSettings) return;

    const lumber = getLumberForMemberTypeOrDefault(memberType, materialSettings, component.definitionType);
    // FIXME Error-handling/spinner?
    await dispatch(loadAdditionalLumberInformation(lumber));
    const lumberWidthIsMemberWidth = lumber.actualThickness === component.thickness;
    const thickness = lumberWidthIsMemberWidth ? lumber.actualThickness : lumber.actualWidth;
    const width = lumberWidthIsMemberWidth ? lumber.actualWidth : lumber.actualThickness;

    const createdRelativeGeometries = new Map<string, RelativeNonBraceMemberGeometry>();
    const createdReferenceLines: CalculatedLine[] = [];

    for (const memberLine of createdMemberLines) {
      const { relativeGeometry, newReferenceLines } = createMemberRelativeGeometry(
        componentDesign.component,
        memberLine.guid,
        memberLine,
        memberLine.name,
        width,
        { locationHint: locationHint ?? null, guessLineLocation: true },
      );

      createdRelativeGeometries.set(memberLine.guid, relativeGeometry);

      for (const referenceLine of newReferenceLines) {
        createdReferenceLines.push(referenceLine);
      }
    }

    const createdMembers = createdMemberLines.map((memberLine) =>
      createMember({
        lumber,
        thickness,
        relativeGeometry: createdRelativeGeometries.get(memberLine.guid)!,
        name: memberLine.name,
        memberType,
        isFieldInstalled: false,
      }),
    );

    dispatch(finishInsertingMembers(createdMembers, createdReferenceLines));
  };

function createMemberRelativeGeometry(
  component: Component,
  definitionLineGuid: string,
  createdDefinitionLine: CalculatedLine | null,
  memberName: string,
  memberWidth: number,
  cutMemberOptions: CutMemberOptions,
): { relativeGeometry: RelativeNonBraceMemberGeometry; newReferenceLines: CalculatedLine[] } {
  let relativeGeometry: RelativeNonBraceMemberGeometry = {
    guid: crypto.randomUUID(),
    lineGuid: definitionLineGuid,
    lineLocation: 'Center',
    width: memberWidth,
    baseCutGuids: [],
    endCutGuids: [],
    memberName,
    geometryType: 'Member',
    isBrace: false,
  };

  const componentWithCreatedDefinitionLine: Component = createdDefinitionLine
    ? {
        ...component,
        referenceLines: [...(component.referenceLines ?? []), createdDefinitionLine],
      }
    : component;

  try {
    const { baseCutGuids, endCutGuids, lineLocation } = cutMember(
      relativeGeometry,
      componentWithCreatedDefinitionLine,
      cutMemberOptions,
    );
    relativeGeometry = { ...relativeGeometry, endCutGuids, baseCutGuids, lineLocation };
  } catch {
    // continue regardless of error
  }

  return { relativeGeometry, newReferenceLines: createdDefinitionLine ? [createdDefinitionLine] : [] };
}

export const finishInsertingBraceMember =
  (memberToBrace: Member, clickedPosition: Point2DInterface): ThunkActionVoid =>
  (dispatch, getState) => {
    const componentDesign = getActiveComponentDesign(getState());
    if (!componentDesign) throw new Error('Active component design not found.');
    const { component } = componentDesign;
    if (!isTruss(component)) {
      dispatch(enqueueError('Adding members to walls is not currently supported.'));
      return;
    }

    const memberType = 'Scab';
    const name = getNewMemberName(
      memberType,
      getAllMembers(component).map((member) => member.name),
    );

    const lumber = memberToBrace.lumber;
    const lumberWidthIsMemberWidth = lumber.actualWidth >= lumber.actualThickness;
    const thickness = lumberWidthIsMemberWidth ? lumber.actualThickness : lumber.actualWidth;
    const width = lumberWidthIsMemberWidth ? lumber.actualWidth : lumber.actualThickness;

    const relativeGeometryResult = createBraceRelativeGeometry(
      componentDesign.component,
      memberToBrace,
      clickedPosition,
      name,
      width,
    );
    if (!relativeGeometryResult) {
      dispatch(enqueueError('Failed to create default brace geometry.'));
      return;
    }
    const { relativeGeometry, newReferenceLines } = relativeGeometryResult;
    const member = createMember({
      lumber,
      thickness,
      relativeGeometry,
      name,
      memberType,
      isFieldInstalled: true,
    });

    dispatch(finishInsertingMembers([member], newReferenceLines));
  };

function createBraceRelativeGeometry(
  component: Component,
  memberToBrace: Member,
  clickedPosition: Point2DInterface,
  memberName: string,
  memberWidth: number,
): {
  relativeGeometry: RelativeBraceGeometry;
  newReferenceLines: CalculatedLine[];
} | null {
  if (memberToBrace.relativeGeometry?.isBrace !== false) return null;

  const definitionLine = component.referenceLines!.find(
    (referenceLine) => referenceLine.guid === memberToBrace.relativeGeometry!.lineGuid,
  )!;
  const definitionLineGeometry = calculateLine(definitionLine, component);
  if (!definitionLineGeometry) return null;
  const projectedClickedPoint = Point2D.fromObject(clickedPosition).projectOntoLine(definitionLineGeometry);
  const isHorizontal = definitionLineGeometry.direction.isParallelTo(Direction2D.right);
  const interceptPoint = definitionLineGeometry.intersectWithLine(isHorizontal ? Line2D.yAxis : Line2D.xAxis)!;
  const standardReferenceLineGuid = isHorizontal ? component.verticalZeroGuid : component.horizontalZeroGuid;
  if (!standardReferenceLineGuid) {
    console.error('Could not find standard reference line');
    return null;
  }

  const offsetMagnitude = projectedClickedPoint.distanceTo(interceptPoint);
  let perpendicularAngle = Direction2D.right.angleTo(definitionLineGeometry.direction) + rightAngle;
  while (angleGreaterThanOrEqual(perpendicularAngle, rightAngle)) perpendicularAngle -= straightAngle;
  const perpendicularOffsetDirection = Direction2D.fromAngle(perpendicularAngle + rightAngle);
  const directionToClickedPoint = Direction2D.fromPoints(interceptPoint, projectedClickedPoint);
  const offsetIsNegative =
    directionToClickedPoint.hasDirection() && !directionToClickedPoint.equals(perpendicularOffsetDirection);
  const offset = offsetIsNegative ? -offsetMagnitude : offsetMagnitude;

  const scabInsertionLine: CalculatedLine = {
    lineType: 'Perpendicular',
    name: 'Brace center line',
    guid: crypto.randomUUID(),
    basePointLine1Guid: definitionLine.guid,
    basePointLine2Guid: standardReferenceLineGuid,
    lineGuid: definitionLine.guid,
    offset,
  };
  // If changing or removing the cut lines below, consider the comment on ScabLengthDimension in SelectedMemberDimensions.
  const baseCut: CalculatedLine = {
    lineType: 'Parallel',
    name: 'Brace base cut',
    guid: crypto.randomUUID(),
    lineGuid: scabInsertionLine.guid,
    offset: -24,
  };
  const endCut: CalculatedLine = { ...baseCut, name: 'Brace end cut', guid: crypto.randomUUID(), offset: 24 };

  const relativeGeometry: RelativeBraceGeometry = {
    guid: crypto.randomUUID(),
    lineGuid: definitionLine.guid,
    // Use the line location from the `memberToBrace` so that subsequent changes to the brace's size will cause it to
    // grow in the same way as the `memberToBrace`.
    lineLocation: memberToBrace.relativeGeometry.lineLocation,
    width: memberWidth,
    baseCutGuids: [baseCut.guid],
    endCutGuids: [endCut.guid],
    memberName,
    geometryType: 'Member',
    braceType: 'Scab',
    braceInstallationLocation: 'Front',
    side: 'Center',
    bracedMemberGuid: memberToBrace.guid,
    isBrace: true,
  };

  return { relativeGeometry, newReferenceLines: [scabInsertionLine, baseCut, endCut] };
}

const finishInsertingMembers =
  (members: Member[], referenceLines: CalculatedLine[]): ThunkActionVoid =>
  (dispatch, getState) => {
    const { insertingObjectInfo } = getState().application;
    if (!insertingObjectInfo || insertingObjectInfo.type !== 'member') {
      throw new Error('Invalid state.');
    }

    const componentDesignGuid = getActiveComponentDesignGuid(getState());
    if (!componentDesignGuid) throw new Error('Active component design not found.');

    referenceLines = referenceLines.slice();

    for (const member of members) {
      function makeMemberLine(lineLocation: LineLocation) {
        return {
          guid: crypto.randomUUID(),
          name: member.name,
          lineType: 'Member' as const,
          memberGeometryGuid: member.relativeGeometry!.guid,

          lineLocation,
        };
      }

      referenceLines.push(makeMemberLine('Center'), makeMemberLine('Port'), makeMemberLine('Starboard'));
    }

    dispatch(commitMembers({ componentDesignGuid, members, referenceLines }));

    const isBrace = members[0].relativeGeometry?.isBrace; // assume all members are the same type
    dispatch(selectItems(members, isBrace ? 'brace' : 'member'));

    if (getState().panelData.rightToolBarOpen) {
      scrollTo('selectedItemProperties');
    }

    dispatch(enqueueSuccess(`Member${members.length > 0 ? 's' : ''} successfully added.`));

    dispatch(updateFeatures());
  };

export const startInsertBearing = (): ThunkActionVoid => (dispatch, getState) => {
  const activeComponentDesign = getActiveComponentDesign(getState());
  if (!activeComponentDesign) return;

  if (!activeComponentDesign.component.referenceLines) {
    dispatch(enqueueError('Cannot add bearings to a component that does not have reference lines.'));
    return;
  }

  // Ensure the component has snaps where bearings can be inserted.
  // If not, do not proceed with insertion and explain to the user.
  const bearingSnaps = calculateBearingInsertionInfo(activeComponentDesign.component);
  if (bearingSnaps.length === 0) {
    dispatch(
      enqueueError(
        'No suitable places to insert a bearing were found. Try adding a bottom chord along reference line Horizontal 0.',
      ),
    );
    return;
  }

  // Ensure the component design is selected.
  dispatch(selectItem(activeComponentDesign, 'componentDesign', { force: true }));

  dispatch(applicationStartInsertBearing());
};

export const startInsertGusset = (): ThunkActionVoid => (dispatch) => {
  dispatch(applicationOnInsertingGusset());
  dispatch(applicationOnPickingGussetLocation());
  dispatch(enqueueInfo('Click a point to center the gusset on.'));
};

export const finishInsertGusset =
  (height: number, width: number, material: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    const activeComponentDesignGuid = getActiveComponentDesignGuid(state);
    if (activeComponentDesignGuid == null) throw new Error('No active component design');

    const { insertingObjectInfo } = state.application;
    if (!insertingObjectInfo || insertingObjectInfo?.type !== 'gusset') throw new Error('Not inserting gusset');

    const { centerPoint } = insertingObjectInfo;

    const spinnerId = dispatch(addSpinnerMessage('Creating gusset...'));
    try {
      const updatedComponentDesign = await designService.componentDesigns.postCreateGusset(
        activeComponentDesignGuid,
        centerPoint!,
        height,
        width,
        material,
      );
      dispatch(setUnsavedComponentDesignFromServer(updatedComponentDesign));
      dispatch(clearInsertingObject());
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(exception, 'Error creating gusset')));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const getLumberForMemberTypeOrDefault = (
  memberType: MemberType,
  materialSettings: MaterialSettingsPair,
  definitionType: TrussDefinitionType,
) => {
  switch (definitionType) {
    case 'FeaturePattern': {
      const { featurePatternMaterialSettings } = materialSettings;
      if (!featurePatternMaterialSettings) return defaultLumber;
      switch (memberType) {
        case 'TopChord':
          return featurePatternMaterialSettings.topChordLumber;
        case 'BottomChord':
          return featurePatternMaterialSettings.bottomChordLumber;
        case 'Web':
        case 'NonstructuralBlock':
          return featurePatternMaterialSettings.webLumber;
        case 'BearingBlock':
          return featurePatternMaterialSettings.bearingBlockLumber;
        case 'EndVertical':
          return featurePatternMaterialSettings.endVerticalLumber;
        case 'GableStud':
          return featurePatternMaterialSettings.gableStudLumber;
        case 'Slider':
          return featurePatternMaterialSettings.sliderLumber;
        case 'RibbonBlock':
          return featurePatternMaterialSettings.ribbonBlockLumber;
        default:
          return defaultLumber;
      }
    }
    case 'Profile': {
      const { profileMaterialSettings } = materialSettings;
      if (!profileMaterialSettings) return defaultLumber;
      switch (memberType) {
        case 'TopChord':
          return profileMaterialSettings.topChordLumber;
        case 'BottomChord':
          return profileMaterialSettings.bottomChordLumber;
        case 'Web':
          return profileMaterialSettings.webLumber;
        case 'BearingBlock':
          return profileMaterialSettings.bearingBlockLumber;
        case 'EndVertical':
          return profileMaterialSettings.endVerticalLumber;
        case 'GableStud':
          return profileMaterialSettings.gableStudLumber;
        case 'Slider':
          return profileMaterialSettings.sliderLumber;
        case 'Wedge':
          return profileMaterialSettings.wedgeLumber;
        default:
          return defaultLumber;
      }
    }
    default:
      assertExhaustiveSwitch(definitionType);
  }
};

export const getNewMemberName = (memberType: MemberType, existingMemberNames: readonly string[]) => {
  const memberTypePrefix = getPrefixForMemberName(memberType);
  const existingMemberNamesWithPrefix = existingMemberNames.filter((name) => name.startsWith(memberTypePrefix));
  let index = 1;
  while (existingMemberNamesWithPrefix.includes(`${memberTypePrefix}${index}`)) {
    index += 1;
  }
  return `${memberTypePrefix}${index}`;
};

const getPrefixForMemberName = (memberType: MemberType) => {
  switch (memberType) {
    case 'TopChord':
      return 'T';
    case 'BottomChord':
      return 'B';
    case 'Web':
      return 'W';
    case 'BearingBlock':
      return 'BB';
    case 'EndVertical':
      return 'EV';
    case 'GableStud':
      return 'ST';
    case 'Slider':
      return 'SL';
    case 'Wedge':
      return 'WG';
    case 'Scab':
      return 'SB';
    case 'ExtraplanarBrace':
      return 'EB';
    default:
      return 'M';
  }
};

export const insertionBearingWidth = 3.5;
export const insertionBearingHeight = 3;

export const insertBearing =
  (insertion: BearingInsertion): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    if (!isInsertingBearing(state.application.insertingObjectInfo)) throw new Error('Invalid state.');

    const componentDesign = getActiveComponentDesign(state);
    if (!componentDesign) throw new Error('Active component design not found.');
    const { component } = componentDesign;
    if (!isTruss(component)) throw new Error('Active component design was not a truss');

    const { bearingDefinitionPoint, justification, trussSide, referenceLineGuid } = insertion;

    let bearingName = 'A';
    while (component.bearings.find((bearing) => bearing.name === bearingName)) {
      bearingName = String.fromCharCode(bearingName.charCodeAt(0) + 1);
    }

    const lineGuid = trussSide === 'Bottom' ? component.verticalZeroGuid : component.horizontalZeroGuid;
    if (lineGuid == null) {
      const typeOfBearing = trussSide === 'Bottom' ? 'horizontal' : 'vertical';
      const missingLine = trussSide === 'Bottom' ? 'Vertical 0' : 'Horizontal 0';
      dispatch(enqueueError(`Cannot insert a ${typeOfBearing} bearing when ${missingLine} does not exist.`));
      return;
    }

    const bearingDefinitionLine: ParallelLine = {
      guid: crypto.randomUUID(),
      name: `Bearing ${bearingName} Definition`,
      lineType: 'Parallel',
      lineGuid,
      offset: trussSide === 'Bottom' ? bearingDefinitionPoint.x : bearingDefinitionPoint.y,
    };

    const lineLocation =
      justification === 'Center' || trussSide === 'Right'
        ? justification
        : justification === 'Port'
          ? 'Starboard'
          : 'Port';

    const bearing: Bearing = {
      guid: crypto.randomUUID(),
      name: bearingName,
      bearingType: trussSide === 'Bottom' ? 'DoublePlate' : 'ToeNail',
      bearingCondition: 'Automatic',
      releaseForUplift: false,
      depth: component.thickness,
      connectors: [],
      relativeGeometry: {
        guid: crypto.randomUUID(),
        bearingName,
        geometryType: 'Bearing',
        bearingGeometryType: 'Bearing',
        lineGuid: bearingDefinitionLine.guid,
        bearingLineGuid: referenceLineGuid,
        lineLocation,
        directionReversed: trussSide === 'Right',
        width: insertionBearingWidth,
      },
    };

    const centerLine: MemberLine = {
      guid: crypto.randomUUID(),
      lineLocation: 'Center',
      lineType: 'Member',
      memberGeometryGuid: bearing.relativeGeometry.guid,
    };
    const portLine: MemberLine = { ...centerLine, guid: crypto.randomUUID(), lineLocation: 'Port' };
    const starboardLine: MemberLine = { ...centerLine, guid: crypto.randomUUID(), lineLocation: 'Starboard' };

    dispatch(
      commitBearing({
        componentDesignGuid: componentDesign.guid,
        bearing,
        referenceLines: [bearingDefinitionLine, centerLine, portLine, starboardLine],
      }),
    );
    dispatch(selectItem(bearing, 'bearing'));
  };

export const startInsertPlatePair =
  (initialPosition?: Point2DInterface): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();

    const activeComponentDesign = getActiveComponentDesign(state);
    const inventoryPlates = getUniquePlatesInMaterialSetForActiveComponentDesign(state);

    const initialPlateType = activeComponentDesign
      ? getMostCommonPlateType(activeComponentDesign.component.platePairs)
      : undefined;

    const initialPlateSize = initialPlateType
      ? getPlateDimensionOptions(inventoryPlates, initialPlateType)[0]
      : undefined;

    dispatch(applicationOnInsertingPlatePair({ initialPosition, initialPlateSize }));
  };

export const updateAndPotentiallyCommitInsertionPlatePair =
  (changes: Partial<InsertingPlatePairInfo>): ThunkActionVoid =>
  (dispatch, getState) => {
    dispatch(updateInsertionPlatePair(changes));

    const {
      application: { insertingObjectInfo },
    } = getState();
    if (insertingObjectInfo?.type !== 'platePair') throw new Error();

    const inventoryPlates = getUniquePlatesInMaterialSetForActiveComponentDesign(getState());

    const { plateType, tangiblePlateTypesForComboPlateType, position, memberGuid, plateDimensions, platePlacement } =
      insertingObjectInfo;
    if (
      plateType &&
      tangiblePlateTypesForComboPlateType &&
      position &&
      memberGuid &&
      plateDimensions &&
      inventoryPlates.some(
        (plateOption) =>
          plateTypeWithTangibleTypesEqualityComparator(plateOption, {
            plateType,
            tangiblePlateTypesForComboPlateType,
          }) &&
          distanceEquals(plateOption.width, plateDimensions.width) &&
          distanceEquals(plateOption.length, plateDimensions.length) &&
          plateOption.geometryType === plateDimensions.geometryType,
      )
    ) {
      dispatch(
        commitInsertPlatePair(
          plateType,
          position,
          memberGuid,
          plateDimensions.width,
          plateDimensions.length,
          plateDimensions.geometryType,
          platePlacement,
        ),
      );
    }
  };

const commitInsertPlatePair =
  (
    plateType: PlateType,
    position: { x: number; y: number },
    memberGuid: string,
    width: number,
    length: number,
    geometryType: PlateGeometryType,
    platePlacement: PlatePlacement,
  ): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const componentDesign = getActiveComponentDesign(state);

    if (!componentDesign) return;
    const { component } = componentDesign;

    if (!isTruss(component)) {
      enqueueError('Adding plates to walls is not currently supported.');
      return;
    }

    const member = component.members.find((m) => m.guid === memberGuid);
    if (!member) throw new Error('Member not found while inserting top/bottom plate');

    const { materialLists } = state;
    if (materialLists.state !== 'loaded') {
      dispatch(enqueueError('Material lists not loaded. Please try again later and/or refresh the page.'));
      return;
    }
    const plateOptionsFromPlateList =
      materialLists.item.plateLists[componentDesign.materialSelection.plateListGuid].plates;

    const commonPlatePairProperties: Omit<
      PlatePair,
      'angleX' | 'angleY' | 'angleZ' | 'distanceBetweenPlates' | 'centerX' | 'centerY'
    > = {
      length,
      name: getNextManuallyInsertedPlatePairName(state),
      centerZ: member.thickness / 2,
      plateType,
      tangiblePlateTypesForComboPlateType: isComboPlateType(plateType)
        ? getTangiblePlateTypesForComboPlatePair(plateType, width, length, geometryType, plateOptionsFromPlateList)
        : [],
      platePlacement,
      width,
      unmergedShearLines: [],
      mergedShearLines: [],
      fabricationTolerancePercentage: null,
      guid: crypto.randomUUID(),
      geometryType,
      hingeAngle: null,
    };
    let platePair: PlatePair;
    if (platePlacement === 'TopBottom') {
      const memberMidline = LineSegment2D.fromSegmentObject(calculateMemberMidline(member, component)!);
      const distanceBetweenPlates =
        member.lumber.actualThickness === member.thickness ? member.lumber.actualWidth : member.lumber.actualThickness;
      const center = Point2D.fromObject(position).projectOntoLine(Line2D.fromLineSegment(memberMidline));

      platePair = {
        angleX: 90,
        angleY: Direction2D.right.angleTo(memberMidline.direction),
        angleZ: 0,
        distanceBetweenPlates,
        centerX: center.x,
        centerY: center.y,
        ...commonPlatePairProperties,
      };
    } else {
      let angleZ: number;
      switch (geometryType) {
        case 'Rectangular':
          angleZ = 90;
          break;
        case 'Hinged':
        case 'TPlate':
          angleZ = 0;
          break;
        case 'BevelPlateLeftHanded':
          angleZ = 135;
          break;
        case 'BevelPlateRightHanded':
          angleZ = 45;
          break;
        default:
          assertExhaustiveSwitch(geometryType);
      }
      platePair = {
        angleX: 0,
        angleY: 0,
        angleZ,
        distanceBetweenPlates: member.thickness,
        centerX: position.x,
        centerY: position.y,
        ...commonPlatePairProperties,
      };
    }

    dispatch(
      upsertPlatePairs({ componentDesignGuid: componentDesign.guid, platePairs: [platePair], lockPlating: true }),
    );
    dispatch(loadAdditionalPlatePairInformation([platePair]));
    dispatch(clearInsertingObject());
    dispatch(selectItem(platePair, 'platePair'));
  };

const cancelInsertionHelper = (): ThunkActionVoid => (dispatch, getState) => {
  const {
    application: { insertingObjectInfo },
    editor: { editInProgress },
  } = getState();

  if (editInProgress === 'EditingReferenceLineIntersection' || editInProgress === 'InsertingMember') {
    dispatch(setEditInProgress(false));
  }

  dispatch(clearInsertingObject());

  if (insertingObjectInfo && insertingObjectInfo.type === 'referenceLine') {
    dispatch(selectItem(getActiveComponentDesign(getState())!, 'componentDesign', { force: true }));
  }

  if (insertingObjectInfo && insertingObjectInfo.type === 'componentDesign' && insertingObjectInfo.oldComponentDesign) {
    dispatch(selectItem(insertingObjectInfo.oldComponentDesign, 'componentDesign', { force: true }));
  }

  if (insertingObjectInfo?.type === 'roofPlane' || insertingObjectInfo?.type === 'bearingEnvelope') {
    dispatch(autoCutRecentlyInsertedPlanes()); // Deliberately not awaited
  }
};

export const cancelInsertComponentDesign = (): ThunkActionVoid => (dispatch, getState) => {
  dispatch(abortComponentDesignInsertion());
  dispatch(cancelInsertionHelper());

  const state = getState();

  const designs = Object.values(state.componentDesigns.loadedState);
  if (isLayoutModeEnabled(state) && designs.filter((design) => design.committed).length === 0) {
    dispatch(setRenderModeAndEnsureSelectedItemIsAppropriate('layout', { force: true }));
  }
};

export const cancelInsertion = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();

  if (isInsertingComponentDesign(state)) {
    dispatch(cancelInsertComponentDesign());
  } else {
    dispatch(cancelInsertionHelper());
  }
};

export const startSelectMemberCut =
  (member: Member, isForBaseCut: boolean): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();

    const activeComponent = getActiveComponentDesign(state)!.component;
    if (!getAllMembers(activeComponent).includes(member)) {
      throw new Error('Attempted to cut member not in active component. This is currently not supported.');
    }

    if (!member.relativeGeometry) {
      throw new Error('Attempted to cut member without relative geometry.  This is not possible.');
    }

    const payload = {
      replacingLineGuid: undefined,
      relativeGeometryGuid: member.relativeGeometry.guid,
      isForBaseCut,
    };

    dispatch(applicationOnPickingMemberCutLineGuid(payload));
  };

export const finishSetReferenceLineForMemberCutGuid =
  (lineGuid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const pickingObjectInfo = getPickingObjectInfo(getState());

    if (pickingObjectInfo && pickingObjectInfo.type === 'pickingReferenceLine') {
      const { targetInfo } = pickingObjectInfo;
      if (targetInfo.type !== 'memberCut') {
        console.assert(false);
        return;
      }
      const componentDesign = getActiveComponentDesign(getState());
      if (!componentDesign) return;
      const { component } = componentDesign;
      const member = getAllMembers(component).find(
        (possibleMember) =>
          !!possibleMember.relativeGeometry && possibleMember.relativeGeometry.guid === targetInfo.relativeGeometryGuid,
      );
      if (!member) return;
      const { isForBaseCut } = targetInfo;
      const cutsToModify = [
        ...(isForBaseCut ? member.relativeGeometry!.baseCutGuids : member.relativeGeometry!.endCutGuids),
      ];
      if (targetInfo.replacingLineGuid) {
        cutsToModify.splice(cutsToModify.indexOf(targetInfo.replacingLineGuid), 1, lineGuid);
      } else {
        cutsToModify.push(lineGuid);
      }
      const newRelativeGeometry = {
        ...member.relativeGeometry!,
        [isForBaseCut ? 'baseCutGuids' : 'endCutGuids']: cutsToModify,
      };
      const newMember = { ...member, relativeGeometry: newRelativeGeometry };
      dispatch(setMembers({ componentDesignGuid: componentDesign.guid, members: [newMember] }));
      dispatch(clearPickingObject());
      dispatch(updateFeatures());
      return;
    }
    console.assert(false);
  };

export const getMemberLine = (
  referenceLines: readonly CalculatedLine[],
  memberGeometryGuid: string,
  location: LineLocation,
): MemberLine | undefined =>
  referenceLines.find(
    (line): line is MemberLine =>
      line.lineType === 'Member' && line.memberGeometryGuid === memberGeometryGuid && line.lineLocation === location,
  );

export const finishSetGablingStartFromProfileJoint =
  (x: number): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const pickingObjectInfo = getPickingObjectInfo(state);

    if (pickingObjectInfo?.type === 'pickingProfileJoint') {
      const componentDesign = getActiveComponentDesign(state);
      if (!componentDesign || !isTruss(componentDesign.component)) return;
      const { component } = componentDesign;
      const newGableSettings = { ...component.gableSettings, studStartLocation: x };
      dispatch(updateActiveGableSettings(newGableSettings));
      dispatch(clearPickingObject());
      dispatch(updateFeatures());
      return;
    }

    console.assert(false);
  };

export const removeMemberCutReferenceLine =
  (member: Member, componentDesignGuid: string, isForBaseCut: boolean, index: number): ThunkActionVoid =>
  (dispatch) => {
    if (!member.relativeGeometry) return;
    const cutsToModify = [
      ...(isForBaseCut ? member.relativeGeometry.baseCutGuids : member.relativeGeometry.endCutGuids),
    ];
    // members must have at least one base or end cut
    if (cutsToModify.length < 2) return;
    if (index >= 0 && index < cutsToModify.length) {
      cutsToModify.splice(index, 1);
    } else return;
    const newRelativeGeometry = {
      ...member.relativeGeometry,
      [isForBaseCut ? 'baseCutGuids' : 'endCutGuids']: cutsToModify,
    };
    const newMember = { ...member, relativeGeometry: newRelativeGeometry };
    dispatch(setMembers({ componentDesignGuid, members: [newMember] }));
    dispatch(updateFeatures());
  };

export type ReferenceLineWithReferenceLineComponents = ParallelLine | PerpendicularLine | DivisionLine;

export const startSetReferenceLineGuid =
  <T extends ReferenceLineWithReferenceLineComponents, K extends KeyToValueOfType<T, string | null>>(
    line: T,
    lineGuidKey: K,
  ): ThunkActionVoid =>
  (dispatch) => {
    dispatch(
      applicationOnPickingReferenceLine({
        rootGuid: line.guid,
        lineGuidKey: lineGuidKey as string,
      }),
    );
  };

export const finishSetReferenceLineGuid =
  (rootGuid: string, key: string, newLineGuid: string, componentDesignGuid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const componentDesign = state.componentDesigns.data[componentDesignGuid];
    const referenceLines =
      getActiveComponentDesign(state)?.guid === componentDesign?.guid
        ? getReferenceLinesForActiveComponent(state)
        : componentDesign?.component.referenceLines;

    if (!componentDesign || !referenceLines) {
      throw new Error('Could not find reference lines for component.');
    }

    const targetLine = referenceLines.find((referenceLine) => referenceLine.guid === rootGuid);
    if (
      !targetLine ||
      getPickingObjectInfoType(state) !== 'pickingReferenceLine' ||
      !(
        targetLine.lineType === 'Parallel' ||
        targetLine.lineType === 'Perpendicular' ||
        targetLine.lineType === 'Division'
      )
    ) {
      throw new Error('Invalid state.');
    }

    dispatch(findAndSetReferenceLine({ ...targetLine, [key]: newLineGuid }));
    dispatch(clearPickingObject());

    if (state.application.insertingObjectInfo?.type !== 'referenceLine') {
      dispatch(updateFeatures());
    }
  };

export const finishSetMemberLine =
  (newLineGuid: string, locationHint?: Point2DInterface): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const pickingObjectInfo = getPickingObjectInfo(getState());

    if (
      !pickingObjectInfo ||
      pickingObjectInfo.type !== 'pickingReferenceLine' ||
      pickingObjectInfo.targetInfo.type !== 'memberLine'
    ) {
      throw new Error();
    }

    const { targetInfo } = pickingObjectInfo;
    const activeComponentDesign = getActiveComponentDesign(getState())!;
    const member = activeComponentDesign.component.members.find(
      (possibleMember) => possibleMember.guid === targetInfo.memberGuid,
    );

    if (member && !member.relativeGeometry?.isBrace) {
      await dispatch(setMemberLine(activeComponentDesign, member, newLineGuid, locationHint));
      dispatch(clearPickingObject());
      return;
    }

    console.assert(false);
  };

export const startSetReferenceLineIntersection =
  <T extends ReferenceLineWithIntersectionComponents>(
    line: T,
    lineGuidKey1: keyof T,
    lineGuidKey2: keyof T,
  ): ThunkActionVoid =>
  (dispatch) => {
    dispatch(
      applicationOnPickingReferenceLineIntersection({
        rootGuid: line.guid,
        lineGuidKey1,
        lineGuidKey2,
      }),
    );
  };

export const finishSetReferenceLineIntersectionGuid =
  (rootGuid: string, line1Guid: string, line2Guid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const { activeComponentDesignGuid } = state.selections;
    const pickingObjectInfo = getPickingObjectInfo(state);

    const componentDesign =
      activeComponentDesignGuid != null ? state.componentDesigns.data[activeComponentDesignGuid] : undefined;
    const referenceLines = getReferenceLinesForActiveComponent(state);

    if (!componentDesign || !referenceLines) {
      throw new Error('Could not find reference lines for active component.');
    }

    const targetLine = referenceLines.find((referenceLine) => referenceLine.guid === rootGuid);
    if (
      !targetLine ||
      pickingObjectInfo?.type !== 'pickingReferenceLineIntersection' ||
      !(
        targetLine.lineType === 'Perpendicular' ||
        targetLine.lineType === 'PointToPoint' ||
        targetLine.lineType === 'PointSlope'
      )
    ) {
      throw new Error('Invalid state');
    }

    dispatch(
      findAndSetReferenceLine({
        ...targetLine,
        [pickingObjectInfo.referenceLineInfo.lineGuidKey1]: line1Guid,
        [pickingObjectInfo.referenceLineInfo.lineGuidKey2]: line2Guid,
      }),
    );

    dispatch(clearPickingObject());
    dispatch(clearEditInProgress());

    if (state.application.insertingObjectInfo?.type !== 'referenceLine') {
      dispatch(updateFeatures());
    }
  };

export const startInsertSplice = (): ThunkActionVoid => (dispatch, getState) => {
  const selectedEntry = getSingleSelectedItemEntryOrNull(getState());
  const chordGuid =
    selectedEntry?.type === 'member' && ['TopChord', 'BottomChord'].includes(selectedEntry.item.memberType)
      ? selectedEntry.item.guid
      : null;

  dispatch(applicationOnInsertingSplice(chordGuid));
};

export const startInsertLoadCase = () => applicationOnInsertingLoadCase();

export const finishInsertingLoadCase =
  (loadCaseName: string, loadCaseGuid: string, copyManualLoads: boolean = true): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const componentDesign = getActiveComponentDesign(state);
    const loadCase = getLoadCasesForActiveComponentDesign(state).find(
      (possibleLoadCase) => possibleLoadCase.guid === loadCaseGuid,
    );
    const newLoadCaseIndex = getLoadCasesForActiveComponentDesign(state).length + 1;
    if (!loadCase) return;
    const newLoadCase: LoadCase = {
      ...loadCase,
      guid: crypto.randomUUID(),
      name: loadCaseName,
      index: newLoadCaseIndex,
      loads: withNewGuids(copyManualLoads ? loadCase.loads : loadCase.loads.filter((load) => !load.isManualLoad)),
    };
    if (
      state.application.insertingObjectInfo &&
      state.application.insertingObjectInfo.type === 'loadCase' &&
      componentDesign
    ) {
      dispatch(addLoadCase(componentDesign, newLoadCase));
      dispatch(clearInsertingObject());
      dispatch(generateLoadCasesOfActiveComponentDesign());
    }
  };

/**
 * Shallow copy with new GUIDs
 */
const withNewGuids = <T extends { guid: string }>(objectsWithGuids: readonly T[]) =>
  objectsWithGuids.map((object) => ({ ...object, guid: crypto.randomUUID() }));

export const finishInsertingJackGirderConnection =
  (jackGirderConnection: JackGirderConnection): AsyncThunkActionVoid =>
  async (dispatch) => {
    await dispatch(addOrUpdateJackGirderConnection(jackGirderConnection, 'add'));
    dispatch(clearInsertingObject());
  };

const finishInsertingAdditionalLoadSettings =
  (addAdditionalLoadAction: any) =>
  (additionalLoadSettings: ParapetManualLoadSettings | MovingLoadSettings): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const componentDesign = getActiveComponentDesign(state);
    if (!componentDesign) return;
    await dispatch(addAdditionalLoadAction(componentDesign, additionalLoadSettings));
    dispatch(clearInsertingObject());
  };

const addParapetManualLoadToComponentDesign =
  (componentDesign: ComponentDesign, parapetManualLoadSettings: ParapetManualLoadSettings): ThunkActionVoid =>
  (dispatch) => {
    if (componentDesign.additionalLoads == null) {
      console.error('Unable to add parapet wind loads because componentDesign.additionalLoads is null');
      return;
    }
    const newParapetManualLoadSettings = [
      ...componentDesign.additionalLoads.parapetManualLoadSettings,
      parapetManualLoadSettings,
    ];
    const newAdditionalLoads = {
      ...componentDesign.additionalLoads,
      parapetManualLoadSettings: newParapetManualLoadSettings,
    };

    dispatch(setAdditionalLoads({ additionalLoads: newAdditionalLoads, componentDesignGuid: componentDesign.guid }));
  };

const addMovingLoadToComponentDesign =
  (componentDesign: ComponentDesign, movingLoadSettings: MovingLoadSettings): ThunkActionVoid =>
  (dispatch) => {
    if (componentDesign.additionalLoads == null) {
      console.error('Unable to add moving loads because componentDesign.additionalLoads is null');
      return;
    }
    const newMovingLoadSettings = [...componentDesign.additionalLoads.movingLoadSettings, movingLoadSettings];
    const newAdditionalLoads = { ...componentDesign.additionalLoads, movingLoadSettings: newMovingLoadSettings };

    dispatch(setAdditionalLoads({ additionalLoads: newAdditionalLoads, componentDesignGuid: componentDesign.guid }));
  };

export const finishInsertingParapetManualLoadSettings = finishInsertingAdditionalLoadSettings(
  addParapetManualLoadToComponentDesign,
);

export const finishInsertingMovingLoadSettings = finishInsertingAdditionalLoadSettings(addMovingLoadToComponentDesign);

export const finishInsertingManualLoads =
  (manualLoadSettingsArray: ManualLoadSettings[]): ThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const componentDesign = getActiveComponentDesign(state);

    if (!componentDesign) return;

    dispatch(addManualLoads(componentDesign, manualLoadSettingsArray));
    dispatch(clearInsertingObject());
  };

export const startDropTopChords = (): ThunkActionVoid => (dispatch, getState) => {
  const component = getActiveComponent(getState());
  if (!component) return;

  const topChordNonZeroSlopeAngles = component.members
    .filter(({ memberType }) => memberType === 'TopChord')
    .map((topChord) => findAndCalculateLine(topChord.relativeGeometry?.lineGuid ?? null, component)?.direction.angle)
    .filter(notNullish)
    .filter((angle) => !angleEquals(angle, 0))
    .map(Math.abs)
    .map((angle) => (angleGreaterThanOrEqual(angle, 90) ? 180 - angle : angle));

  const mostCommonTopChordSlopeAngle =
    topChordNonZeroSlopeAngles.length === 0
      ? convertRadiansToDegrees(Math.atan(6 / 12)) // Default if none of the top chords on this component are sloped
      : groupTransitively(topChordNonZeroSlopeAngles, angleEquals).toSorted(
          (group1, group2) => group2.length - group1.length,
        )[0][0];

  dispatch(applicationOnStartDropTopChords({ angle: mostCommonTopChordSlopeAngle }));
};

export const dropTopChords =
  (topChords: readonly Member[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const componentDesign = getActiveComponentDesign(getState());
    const insertingObjectInfo = getInsertingObjectInfo(getState());
    if (!componentDesign || insertingObjectInfo?.type !== 'droppedTopChords') throw new Error('Unexpected state');
    const { thickness, angle, fullyCrossing } = insertingObjectInfo;

    const spinnerId = dispatch(addSpinnerMessage(`Dropping top chords...`));
    try {
      const newComponentDesign = await designService.componentDesigns.postDropTopChords(
        componentDesign,
        topChords.map(({ guid }) => guid),
        angle,
        thickness,
        fullyCrossing,
      );
      dispatch(setUnsavedComponentDesignFromServer(newComponentDesign));
    } catch {
      dispatch(enqueueError('Error while dropping top chords.'));
      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    dispatch(clearInsertingObject());
    dispatch(selectItem(getActiveComponentDesign(getState()), 'componentDesign'));
  };

export const startInsertMemberStack = (memberGuid: string) => applicationOnInsertingMemberStack(memberGuid);

export const finishInsertingChordStack =
  (startLocation: number, endLocation: number): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const componentDesign = getActiveComponentDesign(state);
    if (
      state.application.insertingObjectInfo &&
      state.application.insertingObjectInfo.type === 'memberStack' &&
      componentDesign
    ) {
      const { memberGuid } = state.application.insertingObjectInfo;
      const member = componentDesign.component.members.find((possibleMember) => possibleMember.guid === memberGuid)!;
      dispatch(stackChord(componentDesign, member, startLocation, endLocation));
      dispatch(clearInsertingObject());
    }
  };

export const addComponentSplices =
  (componentDesign: ComponentDesign, member: Member, distanceFromLeft: number): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    if (!componentDesign) return;

    const spinnerId = dispatch(addSpinnerMessage(`Splicing ${member.name}...`));

    try {
      const newComponentDesign = await designService.componentDesigns.postAddSplices({
        componentDesign,
        chordGuid: member.guid,
        distanceFromLeft,
      });
      dispatch(setUnsavedComponentDesignFromServer(newComponentDesign));
      dispatch(selectItem(getActiveComponentDesign(getState()), 'componentDesign'));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(exception, `Error splicing ${member.name}.`)));
    } finally {
      dispatch(clearInsertingObject());
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const addLoadCase =
  (componentDesign: ComponentDesign, loadCase: LoadCase): ThunkActionVoid =>
  (dispatch) => {
    if (!componentDesign.additionalLoads) return;
    const newManualLoadCases = componentDesign.additionalLoads.manualLoadCases.slice();
    newManualLoadCases.push(loadCase);
    const newAdditionalLoads = { ...componentDesign.additionalLoads, manualLoadCases: newManualLoadCases };
    dispatch(setAdditionalLoads({ additionalLoads: newAdditionalLoads, componentDesignGuid: componentDesign.guid }));
  };

export const addOrUpdateJackGirderConnection =
  (jackGirderConnection: JackGirderConnection, addOrUpdate: 'add' | 'update'): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Adding girder load...'));

    try {
      const jackComponentDesign = getState().componentDesigns.data[jackGirderConnection.jackGuid];
      if (!jackComponentDesign) throw new Error('Could not find jack component.');

      const jackBearing = jackComponentDesign.component.bearings.find(
        (possibleBearing) => possibleBearing.guid === jackGirderConnection.jackBearingGuid,
      );
      if (!jackBearing) throw new Error('Could not find bearing on jack component.');

      const girderComponentDesign = getState().componentDesigns.data[jackGirderConnection.girderGuid];
      if (!girderComponentDesign) throw new Error('Could not find girder component.');

      const hangerExistsIfReferenced =
        jackGirderConnection.hangerGuid === null ||
        girderComponentDesign.component.hangers.find(({ guid }) => guid === jackGirderConnection.hangerGuid) != null;
      if (!hangerExistsIfReferenced) throw new Error('Could not find hanger associated with connection.');

      const timestamps =
        addOrUpdate === 'add'
          ? await designService.jackGirderConnections.create(jackGirderConnection)
          : await designService.jackGirderConnections.put([jackGirderConnection]);
      dispatch(setComponentDesignTimestamps({ timestamps }));
      dispatch(setJackGirderConnections([jackGirderConnection]));
    } catch (exception) {
      console.error(exception);
      enqueueError('Error adding girder load.  Please contact Paragon Customer Support.');

      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const addManualLoads =
  (componentDesign: ComponentDesign, manualLoadSettings: ManualLoadSettings[]): ThunkActionVoid =>
  (dispatch) => {
    if (componentDesign.additionalLoads == null) {
      console.error('Unable to add manual load because componentDesign.additionalLoads is null');
      return;
    }

    const newManualLoadSettings = [...componentDesign.additionalLoads.manualLoadSettings, ...manualLoadSettings];
    const newAdditionalLoads = { ...componentDesign.additionalLoads, manualLoadSettings: newManualLoadSettings };

    dispatch(setAdditionalLoads({ additionalLoads: newAdditionalLoads, componentDesignGuid: componentDesign.guid }));
  };

export const incrementBasicInsertionPlurality = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();

  const { insertingObjectInfo } = state.application;
  if (insertingObjectInfo?.type === 'workLine') {
    if (getInsertionWorkLineType(state) === 'division') {
      dispatch(setWorkLineInsertionSettings({ divisionQuantity: insertingObjectInfo.settings.divisionQuantity + 1 }));
    }
    return;
  }

  const info = getBasicInsertionInformation(state);
  if (info.inserting) dispatch(setBasicInsertionPlurality(info.definition.settings.plurality + 1));
};

export const decrementBasicInsertionPlurality = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();

  const { insertingObjectInfo } = state.application;
  if (insertingObjectInfo?.type === 'workLine') {
    if (getInsertionWorkLineType(state) === 'division') {
      dispatch(setWorkLineInsertionSettings({ divisionQuantity: insertingObjectInfo.settings.divisionQuantity - 1 }));
    }
    return;
  }

  const info = getBasicInsertionInformation(state);
  if (info.inserting && info.definition.settings.plurality > 1) {
    dispatch(setBasicInsertionPlurality(info.definition.settings.plurality - 1));
  }
};
